import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, DatePicker, Input, Modal, Space, Typography, message, Spin, Tag, notification } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { TrainingAPI } from "@api/TrainingAPI";
import { InfoCircleOutlined } from "@ant-design/icons";

type SessionName = string;

// Your API returns numeric ids
type PlayerId = number;

type PlayerSessionEntry = {
  id: number; // datapoint id for this player-session assignment (for updates)
  player_id: PlayerId;
  total_time: number;
  session: SessionName;
  date: string; // "YYYY-MM-DD"
  first_name: string;
  last_name: string;
};

type SessionEntriesMap = Record<SessionName, PlayerSessionEntry[]>;

type PlayerInfo = {
  id: PlayerId;
  first_name: string;
  last_name: string;
  position?: string;
};

const trainingAPI = new TrainingAPI();



// Save stub: send session -> player_id[]
async function save_session_players(dateISO: string, data: Record<SessionName, PlayerId[]>): Promise<void> {
  await new Promise((r) => setTimeout(r, 300));
  void dateISO;
  void data;
}

function sortEntriesByPlayerId(entries: PlayerSessionEntry[]) {
  return [...entries].sort((a, b) => a.player_id - b.player_id);
}

function removeDatapointFromAll(map: SessionEntriesMap, datapoint_id: number): SessionEntriesMap {
  const next: SessionEntriesMap = {};
  for (const k of Object.keys(map)) {
    next[k] = map[k].filter((e) => e.id !== datapoint_id);
  }
  return next;
}

function findContainer(map: SessionEntriesMap, datapoint_id: number): SessionName | null {
  for (const [session, entries] of Object.entries(map)) {
    if (entries.some((e) => e.id === datapoint_id)) return session;
  }
  return null;
}

function get_player_label(entry: PlayerSessionEntry | null) {

  return (
    <div>
        <Typography.Text strong>{entry?.first_name?.[0]}. {entry?.last_name}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: '5px' }}>({entry?.total_time.toFixed(1)} min)</Typography.Text>
      </div>
  )
}

function DraggablePlayerCard({
  entry,
}: {
  entry: PlayerSessionEntry;
}) {
  // dnd-kit ids must be string | number; we can use number directly
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: entry.id });

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.2 : 1,
    cursor: "grab",
    userSelect: "none",
    padding: "3px 10px",
    borderRadius: 8,
    border: "1px solid #f0f0f0",
    background: "#fff",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {get_player_label(entry)}
    </div>
  );
}

function SessionDropZone({
  session,
  count,
  children,
}: {
  session: SessionName;
  count: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: session });

  return (
    <Card
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{session}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {count} players
          </Typography.Text>
        </Space>
      }
      bodyStyle={{ padding: 12 }}
      style={{ borderRadius: 8, width: "30%" }}
    >
      <div
        ref={setNodeRef}
        style={{
          minHeight: 60,
          maxHeight: 400,
          overflowY: "scroll",
          borderRadius: 8,
          outline: isOver ? "2px solid #1677ff" : "1px solid transparent",
          outlineOffset: 2,
          padding: 2,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {children}
      </div>
    </Card>
  );
}

export default function SessionPlayerBoardNoSortable() {
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const dateISO = useMemo(() => (date ? date.format("YYYY-MM-DD") : ""), [date]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sessions, setSessions] = useState<SessionName[]>([]);
  const [itemsBySession, setItemsBySession] = useState<SessionEntriesMap>({});

  const [activeDatapointId, setActiveDatapointId] = useState<number | null>(null);
  const [playerInfoById, setPlayerInfoById] = useState<Record<number, PlayerInfo>>({});

  const [createOpen, setCreateOpen] = useState(false);
  const [newSessionName, setNewSessionName] = useState("");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // --- YOUR UPDATED SEARCH (fixed to actually store entries, enforce uniqueness, sort) ---
  async function handleSearch() {
    if (!date) return message.error("Pick a date first.");
    setLoading(true);
    try {
      const data = await trainingAPI.getPlayerSessionData(date.format("YYYY-MM-DD"));

      // group by session (values are entries)
      const grouped_by_session: SessionEntriesMap = {};
      (data || []).forEach((entry: PlayerSessionEntry) => {
        const session = entry.session;
        if (!grouped_by_session[session]) grouped_by_session[session] = [];
        grouped_by_session[session].push(entry);
      });

      // enforce uniqueness of player across sessions (defensive)
      const seen = new Set<PlayerId>();
      const cleaned: SessionEntriesMap = {};
      for (const [session, entries] of Object.entries(grouped_by_session)) {
        const uniq = entries.filter((e) => {
          if (seen.has(e.id)) return false;
          seen.add(e.id);
          return true;
        });
        cleaned[session] = sortEntriesByPlayerId(uniq);
      }

      setSessions(Object.keys(cleaned));
      setItemsBySession(cleaned);
      message.success(`Loaded sessions for ${date.format("YYYY-MM-DD")}`);
    } catch (e) {
      console.error(e);
      message.error("Failed to load sessions.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!date) return message.error("Pick a date first.");
    setSaving(true);
    try {
      const mapping: Record<SessionName, {datapoint_id: number, player_id: PlayerId}[]> = {};
      for (const [session, entries] of Object.entries(itemsBySession)) {
        mapping[session] = entries.map((e) => ({datapoint_id: e.id, player_id: e.player_id}));
      }

      const payload = {
        date: date.format("YYYY-MM-DD"),
        mapping: mapping
      }
      
      trainingAPI.updatePlayerSessionManagement(payload).then((resp) => {
        message.success(resp.message || "Save successful.");
      }).catch((e) => {
        console.error(e?.response?.status, e?.response?.data);
        message.error("Error saving changes.");
      });
      
    } catch (e) {
      console.error(e);
      message.error("Save failed.");
    } finally {
      setSaving(false);
    }
  }

  function addSession() {
    const name = newSessionName.trim();
    if (!name) return message.error("Session name is required.");
    if (itemsBySession[name] || sessions.includes(name)) return message.error("Session already exists.");

    setSessions((prev) => [...prev, name]);
    setItemsBySession((prev) => ({ ...prev, [name]: [] }));
    setCreateOpen(false);
    message.success("Session created.");
  }

  function onDragStart(e: DragStartEvent) {
    setActiveDatapointId(Number(e.active.id));
  }

function onDragEnd(e: DragEndEvent) {
  const active = Number(e.active.id);              // datapoint id
  const over = e.over?.id ? String(e.over.id) : null;
  setActiveDatapointId(null);

  if (!over) return;

  const from = findContainer(itemsBySession, active); // find by datapoint id
  const to = over as SessionName;

  if (!from) return;
  if (!itemsBySession[to]) return;
  if (from === to) return;

  setItemsBySession((prev) => {
    const fromEntries = prev[from] ?? [];
    const moving = fromEntries.find((en) => en.id === active);
    if (!moving) return prev;

    const toEntries = prev[to] ?? [];

    // VALIDATION: prevent duplicate player_id in destination session
    const duplicate = toEntries.some(
      (en) => en.player_id === moving.player_id && en.id !== moving.id
    );
    if (duplicate) {
      notification.warning({
        message: 'Player already in session',
        description: (
        <>
          <b>{moving?.first_name?.[0]}. {moving?.last_name}</b> is already in <b>{to}</b>.<br />
          A player can only have 1 entry per session.  
        </>),
        placement: "bottomRight"
      });
      return prev; // no state change
    }

    // remove this datapoint from all sessions (guarantee datapoint uniqueness)
    const removed = removeDatapointFromAll(prev, active);

    // move entry into destination
    const movedEntry: PlayerSessionEntry = { ...moving, session: to };
    const nextTo = sortEntriesByPlayerId([...(removed[to] ?? []), movedEntry]);

    return { ...removed, [to]: nextTo };
  });
}


  // Fetch missing player infos whenever board changes
  // useEffect(() => {
  //   const allIds = new Set<PlayerId>();
  //   Object.values(itemsBySession).forEach((entries) => entries.forEach((e) => allIds.add(e.id)));

  //   const missing = Array.from(allIds).filter((id) => !playerInfoById[id]);
  //   if (missing.length === 0) return;

  //   let cancelled = false;
  //   (async () => {
  //     try {
  //       const infos = await Promise.all(missing.map((id) => get_player_info(id)));
  //       if (cancelled) return;
  //       setPlayerInfoById((prev) => {
  //         const next = { ...prev };
  //         infos.forEach((pi) => (next[pi.id] = pi));
  //         return next;
  //       });
  //     } catch {
  //       // keep silent (avoid toast spam)
  //     }
  //   })();

  //   return () => {
  //     cancelled = true;
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [itemsBySession]);

  // initial load
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const boardReady = sessions.length > 0;

  // Active overlay info
  const activeEntry = () => {
    if (activeDatapointId == null) return null;
    for (const entries of Object.values(itemsBySession)) {
      const found = entries.find((e) => e.id === activeDatapointId);
      if (found) return found;
    }
    return null;
  };

  return (
    <div>
      <Typography.Title level={4} style={{marginBottom: 0, marginTop: '1em'}}>Move Players Between Sessions</Typography.Title>
      <hr style={{marginTop: 0}} />
      <Space style={{ marginBottom: 12 }} wrap>
        <DatePicker 
          value={date ? dayjs(date) : null}
          onChange={(d) => setDate(d)} 
          allowClear={false}
          style={{borderRadius: 8}}
          />
        <Button type="primary" onClick={handleSearch} loading={loading} style={{borderRadius: 8}}>
          Search
        </Button>
        <Button onClick={() => (setNewSessionName(""), setCreateOpen(true))} disabled={loading} style={{borderRadius: 8}}>
          Add New Session
        </Button>
        <Button onClick={handleSave} loading={saving} disabled={!boardReady || loading} style={{borderRadius: 8}}>
          Save
        </Button>
      </Space>

      <div style={{marginTop: '1px', marginBottom: '1em', display: 'flex', alignItems: 'center'}}>
        <InfoCircleOutlined style={{color: '#00000073', marginRight: '8px'}}/>
        <Typography.Text type="secondary">Drag and drop players to move them between sessions.</Typography.Text>
      </div>

      <Spin spinning={loading}>
        {!boardReady ? (
          <Card>
            <Typography.Text type="secondary">No sessions found.</Typography.Text>
          </Card>
        ) : (
          <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: "row",
                gap: 16,
                alignItems: "start",
              }}
            >
              {sessions.map((session) => {
                const entries = itemsBySession[session] ?? [];
                return (
                  <SessionDropZone key={session} session={session} count={entries.length}>
                    {entries.length === 0 ? (
                      <div
                        style={{
                          padding: 12,
                          borderRadius: 8,
                          border: "1px dashed #d9d9d9",
                          color: "#8c8c8c",
                          textAlign: "center",
                        }}
                      >
                        Drop players here
                      </div>
                    ) : (
                      entries.map((entry) => (
                        <DraggablePlayerCard
                          key={entry.id}
                          entry={entry}
                        />
                      ))
                    )}
                  </SessionDropZone>
                );
              })}
            </div>

            <DragOverlay>
              {activeDatapointId != null ? (
                <div style={{ width: 260 }}>
                  <div
                    style={{
                      padding: "2px 10px",
                      borderRadius: 8,
                      border: "1px solid #f0f0f0",
                      background: "#fff",
                      boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                    }}
                  >
                    <div>
                      {get_player_label(activeEntry())}
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </Spin>

      <Modal
        open={createOpen}
        title="Create new session"
        onCancel={() => setCreateOpen(false)}
        onOk={addSession}
        okText="Create"
      >
        <Input
          autoFocus
          placeholder="Session name (unique for this day)"
          value={newSessionName}
          onChange={(e) => setNewSessionName(e.target.value)}
          onPressEnter={addSession}
        />
      </Modal>
    </div>
  );
}
